// commentAttachments.js

/**
 * Helpers for Activity comment attachments (Phase 3).
 */

import { detectNodeTypeFromExtension } from 'utilities/fileMime';
import { generateKey } from 'utilities/Keys';

export const COMMENT_ATTACHMENT_ORIGINS = Object.freeze({
  UPLOAD: 'upload',
  CAMERA: 'camera',
  LINK: 'link',
});

/**
 * @param {unknown} value
 * @returns {string}
 */
function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Infer PHOTO / VIDEO / DOCUMENT / FILE from a File-like object.
 *
 * @param {File|object|null|undefined} file
 * @returns {string}
 */
export function inferAttachmentTypeFromFile(file) {
  const mime = cleanString(file?.type).toLowerCase();
  if (mime.startsWith('image/')) return 'PHOTO';
  if (mime.startsWith('video/')) return 'VIDEO';

  const fromName = detectNodeTypeFromExtension(file?.name || '');
  if (fromName) return fromName;

  if (mime.startsWith('application/') || mime.startsWith('text/')) return 'DOCUMENT';
  return 'FILE';
}

/**
 * Normalize a draft or persisted attachment entry for display / write.
 * Local drafts may use `draftId` before a `nodeKey` exists.
 *
 * @param {object|null|undefined} entry
 * @returns {object|null}
 */
export function normalizeCommentAttachment(entry) {
  if (!entry || typeof entry !== 'object') return null;

  const draftId = cleanString(entry.draftId);
  const nodeKey = cleanString(entry.nodeKey || entry.key);
  if (!draftId && !nodeKey) return null;

  const originRaw = cleanString(entry.origin).toLowerCase();
  const origin = originRaw === COMMENT_ATTACHMENT_ORIGINS.CAMERA
    || originRaw === COMMENT_ATTACHMENT_ORIGINS.LINK
    || originRaw === COMMENT_ATTACHMENT_ORIGINS.UPLOAD
    ? originRaw
    : COMMENT_ATTACHMENT_ORIGINS.UPLOAD;

  const previewUrl = cleanString(
    entry.url_thumb
    || entry.urlThumb
    || entry.previewUrl
    || entry.localUri
    || entry.uri
  ) || null;

  return {
    draftId: draftId || null,
    nodeKey: nodeKey || null,
    type: cleanString(entry.type),
    title: cleanString(entry.title || entry.fileName || entry.name) || null,
    url: cleanString(entry.url) || null,
    url_thumb: previewUrl,
    url_grid: cleanString(entry.url_grid || entry.urlGrid) || null,
    storagePath: cleanString(entry.storagePath) || null,
    origin,
    file: entry.file || null,
    localPreview: Boolean(entry.localPreview || entry.file || (!nodeKey && previewUrl)),
  };
}

/**
 * Build a local (not yet uploaded) draft from a File.
 *
 * @param {File|object} file
 * @param {string} origin
 * @returns {object|null}
 */
export function buildLocalDraftAttachment(file, origin) {
  if (!file) return null;

  const previewUrl = cleanString(file.uri || file.localUri)
    || (typeof URL !== 'undefined' && file instanceof Blob
      ? URL.createObjectURL(file)
      : null);

  const title = cleanString(file.name) || 'Attachment';
  const type = inferAttachmentTypeFromFile(file);

  return {
    draftId: generateKey(),
    nodeKey: null,
    type,
    title,
    url_thumb: previewUrl,
    origin: origin || COMMENT_ATTACHMENT_ORIGINS.UPLOAD,
    file,
    localPreview: true,
  };
}

/**
 * Build a persisted attachment from a node + origin.
 *
 * @param {object|null|undefined} node
 * @param {string} origin
 * @param {{ draftId?: string|null }} [options]
 * @returns {object|null}
 */
export function buildCommentAttachmentFromNode(node, origin, options = {}) {
  const nodeKey = cleanString(node?.key);
  if (!nodeKey) return null;

  // Prefer the original object for immediate display — derivative thumbs
  // (url_thumb / url_grid) are often written as paths before Cloud Functions
  // generate them, which leaves a blank chip until refresh.
  const original = cleanString(node?.url || node?.storagePath) || null;
  const storagePath = cleanString(node?.storagePath || node?.url) || null;

  return normalizeCommentAttachment({
    draftId: options.draftId || null,
    nodeKey,
    type: node?.type,
    title: node?.title,
    url: original,
    storagePath,
    url_thumb: cleanString(node?.url_thumb) || null,
    url_grid: cleanString(node?.url_grid) || null,
    origin,
    localPreview: false,
    file: null,
  });
}

/**
 * @param {object[]|null|undefined} comments
 * @param {Iterable<string>|null|undefined} [extraNodeKeys]
 * @returns {Set<string>}
 */
export function collectCommentAttachmentNodeKeys(comments, extraNodeKeys = null) {
  const keys = new Set();

  (Array.isArray(comments) ? comments : []).forEach((comment) => {
    const attachments = Array.isArray(comment?.attachments) ? comment.attachments : [];
    attachments.forEach((entry) => {
      const normalized = normalizeCommentAttachment(entry);
      if (normalized?.nodeKey) keys.add(normalized.nodeKey);
    });
  });

  if (extraNodeKeys) {
    for (const value of extraNodeKeys) {
      const key = cleanString(value);
      if (key) keys.add(key);
    }
  }

  return keys;
}

/**
 * True when a system event is an "added related" relationship line whose
 * related nodeKey is already represented on a comment attachment.
 *
 * @param {object|null|undefined} event
 * @param {Set<string>|null|undefined} attachmentNodeKeys
 * @returns {boolean}
 */
export function shouldSuppressAttachmentRelationshipEvent(event, attachmentNodeKeys) {
  if (!event || !(attachmentNodeKeys instanceof Set) || attachmentNodeKeys.size === 0) {
    return false;
  }

  const changes = Array.isArray(event.changes) ? event.changes : [];
  if (changes.length === 0) {
    const relatedKey = cleanString(event?.relatedNode?.key || event?.relatedNodeKey);
    if (!relatedKey || !attachmentNodeKeys.has(relatedKey)) return false;
    const changeKind = cleanString(event?.changeKind || event?.kind);
    if (changeKind === 'edge_unlinked') return false;
    return true;
  }

  // Suppress only when every change is an edge_linked for an attached node.
  return changes.every((change) => {
    const changeKind = cleanString(change?.changeKind);
    if (changeKind === 'edge_unlinked') return false;
    if (changeKind && changeKind !== 'edge_linked') return false;

    const relatedKey = cleanString(
      change?.relatedNodeKey
      || change?.relatedNode?.key
      || event?.relatedNode?.key
    );
    return Boolean(relatedKey) && attachmentNodeKeys.has(relatedKey);
  });
}

/**
 * Persistable attachment rows (require nodeKey).
 *
 * @param {object[]|null|undefined} attachments
 * @returns {object[]}
 */
export function sanitizeCommentAttachmentsForWrite(attachments) {
  const seen = new Set();
  const rows = [];

  (Array.isArray(attachments) ? attachments : []).forEach((entry) => {
    const normalized = normalizeCommentAttachment(entry);
    if (!normalized?.nodeKey || seen.has(normalized.nodeKey)) return;
    seen.add(normalized.nodeKey);
    rows.push({
      nodeKey: normalized.nodeKey,
      type: normalized.type || null,
      title: normalized.title,
      url: normalized.localPreview ? null : (normalized.url || null),
      url_thumb: normalized.localPreview ? null : (normalized.url_thumb || null),
      url_grid: normalized.localPreview ? null : (normalized.url_grid || null),
      storagePath: normalized.localPreview ? null : (normalized.storagePath || null),
      origin: normalized.origin,
    });
  });

  return rows;
}

/**
 * Revoke blob: preview URLs on local drafts.
 *
 * @param {object|null|undefined} entry
 */
export function revokeDraftPreviewUrl(entry) {
  const url = cleanString(entry?.url_thumb || entry?.previewUrl);
  if (!url || !url.startsWith('blob:')) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
}
